import type { MetricDetailsRegistry } from '@/app/dashboards/utils/report-form/types';

import { deriveMetricDetails } from '@/app/dashboards/utils/deriveMetricDetails';
import { getAveragedMetricFormula } from '@/app/dashboards/utils/metrics.utils.js';
import { POST_TYPE_SECTION_TITLE } from '@/models/dashboards/metrics.constants';

/**
 * Plain snapshot of a metric's details (no computeds) — safe to call anywhere, incl. util fns and
 * `.map` callbacks. Read inside a `computed`/render it re-runs when metric definitions load. Called
 * once imperatively before `metricsStore.metricDetailsReady`, it returns empty fields (`displayName`
 * `undefined`, `formatMetricValue` → '-'), so gate on `metricDetailsReady` if you need a
 * guaranteed-loaded read outside a reactive context. For reactive refs in a component, use `useMetricDetails`.
 */
export function getMetricDetails(
  metric: string,
  channels: string[],
  metricTypeReportKey: string | undefined,
) {
  return deriveMetricDetails({ metric, channels, metricReportTypeKey: metricTypeReportKey });
}

export function getMetricDescriptionTooltip({
  metricTitle,
  isAveraged,
  baseMetricName,
  baseTooltip,
  formula,
  timeframe,
  postType,
  channels,
}: {
  metricTitle?: string;
  isAveraged?: boolean;
  baseMetricName?: string | null;
  baseTooltip?: string | null;
  formula?: string | null;
  timeframe?: string | null;
  postType?: string | null;
  channels?: string[];
}) {
  let tooltipContent = baseTooltip || undefined;
  if (isAveraged && tooltipContent && baseMetricName) {
    tooltipContent = `${baseMetricName}: ${tooltipContent}`;
  }

  let tooltipFormula = formula;
  if (isAveraged) {
    tooltipFormula =
      getAveragedMetricFormula({
        baseMetricName,
        timeframe,
        postType,
      }) ?? tooltipFormula;
  }
  const postTypeLabel =
    postType && channels && channels.length === 1
      ? `Distribution Type: ${POST_TYPE_SECTION_TITLE[postType as keyof typeof POST_TYPE_SECTION_TITLE] ?? postType}`
      : null;
  const footerParts = [tooltipFormula, postTypeLabel].filter(Boolean);
  const tooltipFooter = footerParts.length > 0 ? footerParts.join('\n\n') : undefined;
  const tooltipIcon =
    tooltipContent || tooltipFooter
      ? {
          title: metricTitle,
          content: tooltipContent,
          footer: tooltipFooter,
        }
      : null;
  return tooltipIcon;
}

export function filterMetricsWithParentMetric(
  channels: string[],
  allMetricDetails: MetricDetailsRegistry,
  metricTypeReportKey: string,
) {
  return (metric: string) =>
    channels.every(
      (channel) => !allMetricDetails?.[channel]?.[metricTypeReportKey]?.[metric]?.parent_metric,
    );
}
